import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  ClipboardList, 
  AlertTriangle, 
  Scale, 
  Users, 
  Calendar, 
  CheckCircle, 
  XCircle,
  Eye,
  Edit,
  Trash2,
  Search,
  Plus,
  TrendingUp,
  FileText,
  MapPin
} from 'lucide-react';

function ViewInspection() {
  // Mock data – in real app this comes from API / useQuery / Zustand / Redux
  const [inspections, setInspections] = useState([
    {
      id: 'INS-2026-001',
      date: '2026-02-05',
      inspector: 'Mustafa Ahmed',
      trader: 'Abebe Import',
      tin: 'ET0123456789',
      commodity: 'Tomato',
      violation: 'Price Gouging',
      action: 'Penalty Issued',
      status: 'Completed',
      location: 'Bole 17/18, Addis Ababa',
      lat: 9.0222,
      lng: 38.7468
    },
    {
      id: 'INS-2026-002',
      date: '2026-02-04',
      inspector: 'Sara Kebede',
      trader: 'Fatima Spices',
      tin: 'ET0987654321',
      commodity: 'Coffee',
      violation: 'Short Weight',
      action: 'Verbal Warning',
      status: 'Completed',
      location: 'Merkato, Addis Ababa',
      lat: 9.0300,
      lng: 38.7400,
    },
    {
      id: 'INS-2026-003',
      date: '2026-02-06',
      inspector: 'Mustafa Ahmed',
      trader: 'Dawit Electronics',
      tin: 'ET5678901234',
      commodity: 'Mobile Charger',
      violation: 'No Violation',
      action: 'No Action',
      status: 'Completed',
      location: 'Piassa, Addis Ababa',
      lat: 9.0333,
      lng: 38.7500,
    },
    {
      id: 'INS-2026-004',
      date: '2026-02-03',
      inspector: 'Sara Kebede',
      trader: 'Biniyam Construction',
      tin: 'ET3456789012',
      commodity: 'Cement',
      violation: 'Price Gouging',
      action: 'Penalty Issued',
      status: 'Completed',
      location: 'Kazanchis, Addis Ababa',
      lat: 9.0220,
      lng: 38.7500,
    },
    {
      id: 'INS-2026-005',
      date: '2026-02-07',
      inspector: 'Mustafa Ahmed',
      trader: 'Ethio Pharma',
      tin: 'ET4567890123',
      commodity: 'Medicine',
      violation: 'Expired Product',
      action: 'Product Seized',
      status: 'Pending Review',
      location: 'Mexico, Addis Ababa',
      lat: 9.0200,
      lng: 38.7600,
    },
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // ────────── STATISTICS ──────────
  const stats = useMemo(() => {
    const total = inspections.length;
    const withViolations = inspections.filter(i => i.violation !== 'No Violation').length;
    const penalties = inspections.filter(i => i.action === 'Penalty Issued').length;
    const uniqueTraders = new Set(inspections.map(i => i.trader)).size;
    const pendingReview = inspections.filter(i => i.status === 'Pending Review').length;
    const completed = inspections.filter(i => i.status === 'Completed').length;
    
    // Get current month inspections
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const thisMonth = inspections.filter(i => {
      const inspectionDate = new Date(i.date);
      return inspectionDate.getMonth() === currentMonth && 
             inspectionDate.getFullYear() === currentYear;
    }).length;
    
    // Get today's inspections
    const today = new Date().toISOString().split('T')[0];
    const todayInspections = inspections.filter(i => i.date === today).length;
    
    // Get recent inspections (last 7 days)
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const recent = inspections.filter(i => new Date(i.date) >= sevenDaysAgo).length;
    
    return { 
      total, 
      withViolations, 
      penalties, 
      uniqueTraders,
      pendingReview,
      completed,
      thisMonth,
      todayInspections,
      recent
    };
  }, [inspections]);

  const filteredInspections = inspections.filter(item =>
    item.trader.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.commodity.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.violation.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openViewModal = (inspection) => {
    setSelectedInspection(inspection);
    setIsViewModalOpen(true);
  };

  const openEditModal = (inspection) => {
    setSelectedInspection(inspection);
    setIsEditModalOpen(true);
  };

  const handleDelete = (id) => {
    if (deleteConfirmId === id) {
      setInspections(prev => prev.filter(item => item.id !== id));
      setDeleteConfirmId(null);
    } else {
      setDeleteConfirmId(id);
    }
  };

  const handleUpdate = (updatedData) => {
    setInspections(prev =>
      prev.map(item =>
        item.id === updatedData.id ? { ...item, ...updatedData } : item
      )
    );
    setIsEditModalOpen(false);
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch(status) {
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'Pending Review':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get violation badge color
  const getViolationColor = (violation) => {
    return violation === 'No Violation' 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <div className="flex items-center gap-3">
                <ClipboardList className="h-10 w-10 text-indigo-600" />
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    Inspection Reports
                  </h1>
                  <p className="text-gray-600 mt-2">View and manage all inspection records</p>
                </div>
              </div>
            </div>
           
          </div>
        </div>

        {/* Statistics Cards - First Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-indigo-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Total Inspections</p>
                <p className="text-3xl font-bold text-gray-800 mt-1">{stats.total}</p>
              </div>
              <div className="bg-indigo-100 rounded-full p-3">
                <ClipboardList className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Violations Found</p>
                <p className="text-3xl font-bold text-gray-800 mt-1">{stats.withViolations}</p>
                <p className="text-xs text-red-600 mt-1">{((stats.withViolations / stats.total) * 100 || 0).toFixed(1)}% of total</p>
              </div>
              <div className="bg-red-100 rounded-full p-3">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>

         
        </div>

       
        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-600 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by ID, trader, commodity or violation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border-2 border-indigo-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none bg-white"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-indigo-700">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">ID</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Date</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden md:table-cell">Inspector</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Trader</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden lg:table-cell">Commodity</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Violation</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden md:table-cell">Action</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden sm:table-cell">Status</th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-white uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInspections.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                      <ClipboardList className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                      <p>No inspections found</p>
                    </td>
                  </tr>
                ) : (
                  filteredInspections.map((item) => (
                    <tr key={item.id} className="hover:bg-indigo-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 text-sm">{item.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700 text-sm">{item.date}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700 text-sm hidden md:table-cell">{item.inspector}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900 text-sm font-medium">{item.trader}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700 text-sm hidden lg:table-cell">{item.commodity}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getViolationColor(item.violation)}`}>
                          {item.violation}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700 text-sm hidden md:table-cell">{item.action}</td>
                      <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(item.status)}`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => openViewModal(item)}
                            className="p-1 text-indigo-600 hover:text-indigo-800 transition rounded-lg hover:bg-indigo-100"
                            title="View Details"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          
                         
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* View Detail Modal */}
        {isViewModalOpen && selectedInspection && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">
                    Inspection Details
                  </h2>
                  <button
                    onClick={() => setIsViewModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Inspection ID</p>
                    <p className="text-base font-medium text-gray-900">{selectedInspection.id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Date</p>
                    <p className="text-base font-medium">{selectedInspection.date}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Inspector</p>
                    <p className="text-base font-medium">{selectedInspection.inspector}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedInspection.status)}`}>
                      {selectedInspection.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Trader</p>
                    <p className="text-base font-medium">{selectedInspection.trader}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">TIN</p>
                    <p className="text-base font-medium">{selectedInspection.tin || '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Commodity</p>
                    <p className="text-base font-medium">{selectedInspection.commodity}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Violation</p>
                    <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${getViolationColor(selectedInspection.violation)}`}>
                      {selectedInspection.violation}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Action Taken</p>
                    <p className="text-base font-medium">{selectedInspection.action}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="text-base font-medium flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      {selectedInspection.location}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      ({selectedInspection.lat.toFixed(5)}, {selectedInspection.lng.toFixed(5)})
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {isEditModalOpen && selectedInspection && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold mb-6">Edit Inspection Report</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Violation</label>
                    <input
                      type="text"
                      defaultValue={selectedInspection.violation}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Action Taken</label>
                    <select 
                      defaultValue={selectedInspection.action} 
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option>Verbal Warning</option>
                      <option>Written Warning</option>
                      <option>Penalty Issued</option>
                      <option>Product Seized</option>
                      <option>Business Temporarily Closed</option>
                      <option>No Action</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Status</label>
                    <select 
                      defaultValue={selectedInspection.status} 
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option>Completed</option>
                      <option>Pending Review</option>
                    </select>
                  </div>
                  <div className="flex flex-col sm:flex-row justify-end gap-3 mt-8">
                    <button
                      onClick={() => setIsEditModalOpen(false)}
                      className="w-full sm:w-auto px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        handleUpdate({
                          ...selectedInspection,
                        });
                      }}
                      className="w-full sm:w-auto px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ViewInspection;